import { useEffect, useState, type JSX } from 'react';
import { Navigate, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import { completeSignIn, isSignedIn, signIn, signOut } from './auth/cognito.js';
import { Library } from './pages/Library.js';
import { TemplateEditor } from './pages/TemplateEditor.js';
import { FillForm } from './pages/FillForm.js';

function AuthCallback(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    const code = params.get('code');
    if (!code) {
      setError('Sign-in did not complete. Please try again.');
      return;
    }
    completeSignIn(code)
      .then(() => navigate('/', { replace: true }))
      .catch((caught: Error) => setError(caught.message));
  }, [params, navigate]);

  if (error) {
    return (
      <main className="page">
        <p className="error" role="alert">
          {error}
        </p>
        <button type="button" className="btn-primary" onClick={() => void signIn()}>
          Try again
        </button>
      </main>
    );
  }

  return <p>Signing you in…</p>;
}

function SignInPrompt(): JSX.Element {
  return (
    <main className="page">
      <h1>Template Filler</h1>
      <p>Sign in to reach your templates.</p>
      <button type="button" className="btn-primary" onClick={() => void signIn()}>
        Sign in
      </button>
      {/* No sign-up link: accounts are created by an administrator (FR-003a). */}
      <p className="hint">
        Accounts are created by an administrator. If you do not have one, ask them to add you.
      </p>
    </main>
  );
}

function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  return isSignedIn() ? children : <SignInPrompt />;
}

export function App(): JSX.Element {
  return (
    <>
      {isSignedIn() ? (
        <nav className="app-nav">
          <button type="button" className="btn-ghost" onClick={signOut}>
            Sign out
          </button>
        </nav>
      ) : null}

      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <Library />
            </RequireAuth>
          }
        />
        <Route
          path="/templates/new"
          element={
            <RequireAuth>
              <TemplateEditor />
            </RequireAuth>
          }
        />
        <Route
          path="/templates/:templateId"
          element={
            <RequireAuth>
              <TemplateEditor />
            </RequireAuth>
          }
        />
        <Route
          path="/templates/:templateId/fill"
          element={
            <RequireAuth>
              <FillForm />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
